// DetailCard — shared shell for slide-over detail cards on the map.
//
// Used by: AirspaceDetail, PirepDetail, NavaidDetail, DipSiteDetail,
// DetailPane. Each shares the same outer container + close button +
// optional header pattern + optional rollup key/value table.
//
// Usage:
//   <DetailCard
//     headerDot="bg-red-500"
//     eyebrow="AIRSPACE · TFR"
//     title="P-51 Restricted"
//     subtitle="Active TFR"
//     onClose={() => set(null)}
//     rollup={[['Floor', 'SFC'], ['Ceiling', 'FL180']]}
//     remarks="Full NOTAM text..."
//     externalLink={{ label: 'FAA TFR site', href: '...' }}
//   />
//
//   — or for components with a custom header structure (e.g. NavaidDetail) —
//
//   <DetailCard onClose={...} header={<>large-ident-jsx</>}>
//     {/* body children */}
//   </DetailCard>

import { type ReactNode } from 'react';

export interface DetailCardProps {
  onClose: () => void;
  children?: ReactNode;

  // ── Standard header props ───────────────────────────────────────────────
  // Provide these to use the standard dot+eyebrow+title+subtitle header.
  headerDot?: string;      // Tailwind color class e.g. 'bg-red-500'
  eyebrow?: ReactNode;     // Small all-caps label row; can be JSX for extras
  headerIcon?: ReactNode;  // Optional icon at far-right of eyebrow row
  title?: string;
  subtitle?: string;

  // When set, replaces the standard header left-side entirely.
  // Use this for components with a structurally different header (NavaidDetail).
  header?: ReactNode;

  // ── Body helpers ────────────────────────────────────────────────────────
  // Simple uniform key/value rollup table. For rows needing custom value
  // color, render a <RollupTable> in children instead.
  rollup?: Array<[string, string]>;
  remarks?: string;
  externalLink?: { label: string; href: string };

  // ── Layout ───────────────────────────────────────────────────────────────
  // Default width is w-96. Pass 'w-80' for narrower variants (DetailPane).
  width?: string;
}

/**
 * Reusable key/value rollup table. Exported so components that need
 * per-row value color (e.g. DipSiteDetail's reimbursement warning) can
 * render their own rows inline.
 */
export function RollupRow({ k, v, valueClass }: { k: string; v: string; valueClass?: string }) {
  return (
    <div className="flex items-start justify-between text-xs gap-3">
      <span className="text-cockpit-300 shrink-0">{k}</span>
      <span className={`font-mono text-right break-words ${valueClass ?? 'text-cockpit-100'}`}>{v}</span>
    </div>
  );
}

export function RollupTable({ rows }: { rows: Array<[string, string]> }) {
  if (!rows.length) return null;
  return (
    <div className="bg-cockpit-700/20 border border-cockpit-700 rounded-md px-3 py-2 space-y-0.5">
      {rows.map(([k, v]) => (
        <RollupRow key={k} k={k} v={v} />
      ))}
    </div>
  );
}

export default function DetailCard({
  onClose,
  children,
  headerDot,
  eyebrow,
  headerIcon,
  title,
  subtitle,
  header,
  rollup,
  remarks,
  externalLink,
  width = 'w-96',
}: DetailCardProps) {
  const hasHeader = header !== undefined || title !== undefined || eyebrow !== undefined;

  return (
    <div className={`absolute z-30 right-3 bottom-20 ${width} max-w-[calc(100vw-1.5rem)] bg-cockpit-800/95 backdrop-blur border border-cockpit-600 rounded-xl shadow-rail`}>
      {hasHeader && (
        <div className="flex items-start justify-between p-3 border-b border-cockpit-700">
          {/* Left side: custom header slot or standard dot+eyebrow+title+subtitle */}
          {header !== undefined ? (
            <div className="min-w-0 flex-1">{header}</div>
          ) : (
            <div className="min-w-0 flex-1">
              {eyebrow !== undefined && (
                <div className="flex items-center gap-2 mb-0.5">
                  {headerDot && (
                    <span className={`inline-block h-2 w-2 rounded-full ${headerDot}`} aria-hidden />
                  )}
                  {typeof eyebrow === 'string' ? (
                    <span className="text-[10px] tracking-widest text-cockpit-300">{eyebrow}</span>
                  ) : (
                    eyebrow
                  )}
                  {headerIcon && headerIcon}
                </div>
              )}
              {title !== undefined && (
                <div className="font-mono text-cockpit-100 text-base leading-tight truncate">{title}</div>
              )}
              {subtitle !== undefined && (
                <div className="text-xs text-cockpit-300 truncate">{subtitle}</div>
              )}
            </div>
          )}

          {/* Close button — always consistent */}
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-cockpit-300 hover:text-cockpit-100 text-sm leading-none px-2"
          >✕</button>
        </div>
      )}

      <div className="p-3 space-y-3 text-sm">
        {rollup && rollup.length > 0 && (
          <RollupTable rows={rollup} />
        )}

        {remarks && (
          <div>
            <div className="text-[10px] tracking-widest text-cockpit-300 mb-1">REMARKS</div>
            <div className="bg-cockpit-700/20 border border-cockpit-700 rounded-md px-3 py-2 text-[11px] text-cockpit-100 whitespace-pre-wrap leading-snug">
              {remarks}
            </div>
          </div>
        )}

        {externalLink && (
          <div>
            <a
              href={externalLink.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 bg-cockpit-700 hover:bg-cockpit-600 border border-cockpit-600 text-cockpit-100 rounded-md px-2.5 py-1 text-[11px]"
            >
              {externalLink.label}
              <span aria-hidden>↗</span>
            </a>
          </div>
        )}

        {children}
      </div>
    </div>
  );
}
